import { useAccountStore } from "@/composables/account/useAccountStore";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import {
  forwardsApi,
  FEASIBILITY_BLOCK_ERROR_TYPE,
  forwardsErrorMessage,
  forwardsErrorType,
  isDomainError,
  isNotFoundError,
} from "@/forwards2/forwardsApi";
import type {
  ListingWrite,
  PoolCommitmentRow,
  PoolDetailResponse,
  PoolDeviceRow,
  PoolListingKind,
  PoolMemberRow,
  PoolPreviewResponse,
} from "@/forwards2/forwardsTypes";
import { isForwardsStaff } from "@/forwards2/forwardsGuard";
import type { PeriodWidth } from "@/forwards2/periodUtils";
import { defaultPoolName } from "@/forwards2/pools/poolDefaultName";
import {
  blankPoolForm,
  cloneForm,
  formFromPool,
  listingFromForm,
  productionRangeOf,
  requestFromForm,
  selectedWidth,
  type PoolEditorForm,
} from "@/forwards2/pools/poolEditorForm";
import {
  listingRowErrors,
  minimumPriceError,
  productionRangeError,
} from "@/forwards2/pools/poolFormRules";
import { debounce, isEqual, sortBy, uniqBy } from "lodash-es";
import {
  computed,
  onScopeDispose,
  reactive,
  readonly,
  ref,
  shallowRef,
  watch,
} from "vue";

/** The editor previews after this quiet period; long enough not to fire per keystroke. */
export const PREVIEW_DEBOUNCE_MS = 400;

export type PoolEditor = ReturnType<typeof usePoolEditor>;

/**
 * The pool editor's state: one form, the live preview of it, and the writes.
 *
 * `PUT /pools/<id>` replaces the pool wholesale, so the form always holds the complete description
 * and every save sends all of it — a body missing the listing policy or `member_company_ids` would
 * delete what it left out. Nothing here throws: every call records an error the view renders inline.
 * A domain error is a sentence the view renders itself; anything else is a bug or an outage, and
 * also goes to the app-wide handler, which shows the correlation code.
 *
 * One editing session's state, so `PoolEditorModal` calls it in its setup with the pool it opens
 * (`null` for a new one): the state and its timers go with the modal. Returned as one reactive
 * object, so a template reads `editor.loading` rather than a ref.
 */
export function usePoolEditor(openedPoolId: number | null) {
  const { globalErrorHandler } = useGlobalHandlers();
  // Changes only when a new pool's create answers with its id.
  const poolId = ref<number | null>(openedPoolId);
  const form = ref<PoolEditorForm>(blankPoolForm());
  const detail = ref<PoolDetailResponse | null>(null);
  const commitments = ref<PoolCommitmentRow[]>([]);
  const preview = ref<PoolPreviewResponse | null>(null);
  const savedForm = ref<PoolEditorForm>(blankPoolForm());
  const candidateDevices = ref<PoolDeviceRow[]>([]);
  // From a membership change, through its debounce, until the latest request answers.
  const devicesLoading = ref(false);
  const candidateCompanies = ref<PoolMemberRow[]>([]);

  const loading = ref(openedPoolId !== null);
  const previewLoading = ref(false);
  // Set from the form change until the debounced preview starts, so nothing judges the form on the
  // previous preview's blockers in between.
  const previewPending = ref(false);
  const saving = ref(false);
  const busyAction = ref<"delete" | "archive" | "unarchive" | null>(null);
  // `shallowRef`: a foreign error object put in a deep `ref` comes back as a reactive proxy, which
  // breaks identity checks and `instanceof` on it (C-09b-11).
  const loadError = shallowRef<unknown>(null);
  const notFound = ref(false);
  /**
   * Whether the name is the user's own. A new pool's name follows the one generated from its
   * companies and devices until `setName` records its creator's; an existing pool's name always is.
   */
  const nameEdited = ref(openedPoolId !== null);
  const saveError = shallowRef<unknown>(null);
  const candidateCompaniesError = shallowRef<unknown>(null);
  const warnings = ref<string[]>([]);

  const isNew = computed(() => poolId.value === null);
  // What a save would send: entries left in a mode that is not selected are never saved, so they are
  // not unsaved changes either.
  const isDirty = computed(
    () =>
      !isEqual(requestFromForm(form.value), requestFromForm(savedForm.value))
  );
  const listingChanged = computed(
    () =>
      !isEqual(listingFromForm(form.value), listingFromForm(savedForm.value))
  );
  const entryWidth = computed(() => selectedWidth(form.value));

  /** The selected width's recurring template; the other widths keep theirs. */
  const recurringDraft = computed(() => form.value.recurring[entryWidth.value]);

  /** The selected width's one-off rows; the other widths keep theirs. */
  const oneOffListings = computed({
    get: () => form.value.one_off[entryWidth.value],
    set: (listings: ListingWrite[]) => {
      form.value.one_off[entryWidth.value] = listings;
    },
  });
  const capabilities = computed(() => detail.value?.capabilities ?? null);
  const canEdit = computed(() =>
    isNew.value ? true : (capabilities.value?.can_edit ?? false)
  );

  /**
   * A pool's companies change only as an account that owns them (user, 2026-10-20), so a super admin
   * acting as themselves edits the rest of a client's pool with its companies read-only.
   */
  const membersLocked = computed(
    () =>
      !isNew.value &&
      canEdit.value &&
      !(capabilities.value?.can_change_members ?? false)
  );

  const accountStore = useAccountStore();
  /** A new pool's companies are offered from a super admin's own account until they manage a client's. */
  const offersOwnCompaniesOnly = computed(
    () =>
      isNew.value &&
      isForwardsStaff(accountStore.account) &&
      !accountStore.isManagingAccount
  );
  const blockers = computed(() => preview.value?.blockers ?? []);
  const previewWarnings = computed(() => preview.value?.warnings ?? []);

  /** A floor the write routes refuse — below zero, or finer than they store; the options card says which beside the field. */
  const minimumPriceProblem = computed(() =>
    minimumPriceError(form.value.minimum_price)
  );

  /** A production range the write routes refuse; a one-off listing sends none, so it has none. */
  const productionRangeProblem = computed(() =>
    form.value.listing_kind === "one_off"
      ? null
      : productionRangeError(productionRangeOf(form.value))
  );

  /**
   * Whether the form states something the write routes would refuse outright.
   *
   * The listing section writes what the user typed into the form — a period the pool cannot state,
   * the same period on two rows, a production range that ends before it starts — and so does the
   * price field for a floor the routes refuse, so that each can explain the mistake beside its
   * field.
   * Such a form is not worth previewing (the route answers 400, and the editor would lose the
   * estimates it is showing) and must not be saved, so `refreshPreview` and `canSave` both stand
   * down while it is true.
   */
  const formInvalid = computed(() => {
    if (minimumPriceProblem.value !== null) return true;
    if (productionRangeProblem.value !== null) return true;
    if (form.value.listing_kind !== "one_off") return false;
    return listingRowErrors(oneOffListings.value).size > 0;
  });

  /** What a former member left behind here; non-null means this is the escrow-only view of the pool. */
  const escrowRemaining = computed(
    () => detail.value?.escrow_remaining ?? null
  );

  /** Whether the form names a different membership than the pool was saved with. */
  const membershipEdited = computed(
    () =>
      !isNew.value &&
      !isEqual(
        sortedIds(form.value.member_company_ids),
        sortedIds(detail.value?.pool.member_company_ids ?? [])
      )
  );

  /**
   * The device panel's rows: the saved pool's own devices, or the candidates of the companies the
   * form names whenever that membership is not the saved one — a company added in this edit has to
   * contribute its devices to the allowlist, or an allowlist pool would need two saves
   * (RV2-pool-lifecycle-08).
   */
  const devices = computed<PoolDeviceRow[]>(() =>
    isNew.value || membershipEdited.value
      ? candidateDevices.value
      : (detail.value?.devices ?? [])
  );

  /**
   * The devices the pool itself sells from, derived from the form the way
   * `pool_device_service.effective_device_ids_for` derives them on the backend, so the eligibility
   * card counts each blocker against the pool's own devices rather than against every candidate of
   * its member companies (D-FIX5-1-01). Allowlist mode is explicit: an empty allowlist covers nothing.
   */
  const poolDevices = computed<PoolDeviceRow[]>(() => {
    if (form.value.device_scope !== "allowlist") return devices.value;
    const allowed = new Set(form.value.allowlist_device_ids);
    return devices.value.filter((device) => allowed.has(device.device_id));
  });

  // Existing members must remain visible when the form replaces the entire membership.
  const memberCandidates = computed<PoolMemberRow[]>(() =>
    sortBy(
      uniqBy(
        [...candidateCompanies.value, ...(detail.value?.members ?? [])],
        (candidate) => candidate.company_id
      ),
      (candidate) => candidate.company_name.toLowerCase()
    )
  );

  const memberNames = computed(() => {
    const names = new Map(
      memberCandidates.value.map((candidate) => [
        candidate.company_id,
        candidate.company_name,
      ])
    );
    return form.value.member_company_ids
      .map((id) => names.get(id))
      .filter((name): name is string => name !== undefined);
  });

  const generatedName = computed(() =>
    defaultPoolName(memberNames.value, poolDevices.value)
  );

  /** A preview is debouncing or in flight: its blockers are not the current form's yet. */
  const previewBusy = computed(
    () => previewPending.value || previewLoading.value
  );

  const canSave = computed(
    () =>
      canEdit.value &&
      form.value.name.trim() !== "" &&
      form.value.member_company_ids.length > 0 &&
      !formInvalid.value &&
      blockers.value.length === 0
  );

  /**
   * Whether the companies and devices let a new pool's wizard continue: companies with no blocker
   * the preview reports (a new pool's blockers are all about its companies and devices), and in
   * allowlist mode at least one device. Held from a form change until its preview answers, so a
   * blocker it is about to report cannot be stepped past.
   */
  const membersReady = computed(
    () =>
      !previewBusy.value &&
      form.value.member_company_ids.length > 0 &&
      blockers.value.length === 0 &&
      (form.value.device_scope !== "allowlist" ||
        form.value.allowlist_device_ids.length > 0)
  );

  /** Whether the listing lets a new pool's wizard continue: one the write routes accept, previewed. */
  const listingReady = computed(() => !previewBusy.value && !formInvalid.value);

  const saveErrorMessage = computed(() =>
    saveError.value === null ? null : forwardsErrorMessage(saveError.value)
  );

  /** Whether `?force=1` would change the outcome of the last refused save — admins only (DSP-03: keyed off `error_type`). */
  const canForceSave = computed(
    () =>
      (capabilities.value?.is_admin ?? false) &&
      forwardsErrorType(saveError.value) === FEASIBILITY_BLOCK_ERROR_TYPE
  );

  // The preview and the candidate devices each let only their own latest request answer, so a
  // slow reply for an older form never lands.
  let previewGeneration = 0;
  let candidateGeneration = 0;

  /** Reads the opened pool into the form; a new pool's blank form needs nothing read. */
  const load = async () => {
    if (poolId.value === null) return;
    loading.value = true;
    try {
      await readPool(poolId.value);
    } catch (caught) {
      loadError.value = caught;
      notFound.value = isNotFoundError(caught);
      reportUnexpected(caught);
    } finally {
      loading.value = false;
    }
  };

  /**
   * Reads the pool again after an archive or unarchive, which change its capabilities and status.
   * The last save's warnings belong to the pool as it was. A failed read leaves what is on screen.
   */
  const reload = async () => {
    warnings.value = [];
    try {
      await readPool(requireId());
    } catch (caught) {
      loadError.value = caught;
      reportUnexpected(caught);
    }
  };

  /**
   * The heading, the membership baseline and the capabilities read `detail`, which the write
   * response does not carry. Not `reload()`: that clears the warnings the view shows after a save.
   * A failed refetch leaves the stale detail in place; the save itself has already succeeded.
   */
  const refreshDetail = async (id: number) => {
    try {
      detail.value = (await forwardsApi.getPool(id)).data;
    } catch (caught) {
      loadError.value = caught;
      reportUnexpected(caught);
    }
  };

  /**
   * Asks the backend what the unsaved form would mean. Errors are swallowed: the preview is a hint.
   *
   * A form with a field error is not sent at all, so the last preview stays on screen while the
   * user fixes it rather than being replaced by nothing (D-TN04b-02). A form without companies is
   * not sent either: there is nothing to preview, and the backend would only answer with the
   * missing-company blocker the members picker already states — on a new pool, before the user
   * has picked anything.
   */
  const refreshPreview = async () => {
    previewPending.value = false;
    if (!canEdit.value || formInvalid.value) return;
    if (form.value.member_company_ids.length === 0) {
      previewGeneration++;
      previewLoading.value = false;
      preview.value = null;
      return;
    }

    const generation = ++previewGeneration;
    previewLoading.value = true;
    try {
      const response = await forwardsApi.previewPool(
        requestFromForm(form.value),
        poolId.value
      );
      if (generation === previewGeneration) preview.value = response.data;
    } catch {
      if (generation === previewGeneration) preview.value = null;
    } finally {
      if (generation === previewGeneration) previewLoading.value = false;
    }
  };

  const schedulePreview = debounce(() => {
    void refreshPreview();
  }, PREVIEW_DEBOUNCE_MS);

  /**
   * Loads the devices the form's companies could sell from.
   *
   * A saved pool whose membership the form has not touched already carries its device rows in the
   * detail response, so only an unsaved pool or an edited membership costs a request — one per
   * membership change, debounced.
   *
   * Errors are swallowed like the preview's: the panel then simply lists nothing, which is also
   * what an empty membership means.
   */
  const refreshCandidateDevices = async () => {
    const generation = ++candidateGeneration;
    if (!isNew.value && !membershipEdited.value) {
      devicesLoading.value = false;
      return;
    }
    const companyIds = [...form.value.member_company_ids];
    if (companyIds.length === 0) {
      candidateDevices.value = [];
      devicesLoading.value = false;
      return;
    }

    devicesLoading.value = true;
    try {
      const response = await forwardsApi.listCandidateDevices(companyIds);
      if (generation === candidateGeneration)
        candidateDevices.value = response.data;
    } catch {
      if (generation === candidateGeneration) candidateDevices.value = [];
    } finally {
      if (generation === candidateGeneration) devicesLoading.value = false;
    }
  };

  const scheduleCandidateDevices = debounce(() => {
    void refreshCandidateDevices();
  }, PREVIEW_DEBOUNCE_MS);

  /**
   * Loads the companies the session may put in a pool, once per editing session: the choices do not
   * depend on the pool. A failure leaves the picker with the pool's own members only.
   */
  const loadCandidateCompanies = async () => {
    try {
      candidateCompanies.value = (
        await forwardsApi.listCandidateCompanies()
      ).data;
      candidateCompaniesError.value = null;
    } catch (caught) {
      candidateCompaniesError.value = caught;
      reportUnexpected(caught);
    }
  };

  /**
   * Creates or replaces the pool.
   *
   * @returns the saved pool's id, or null when the save was refused.
   */
  const save = async (
    options: { force?: boolean } = {}
  ): Promise<number | null> => {
    saving.value = true;
    saveError.value = null;
    try {
      const request = requestFromForm(form.value);
      const existingId = poolId.value;
      const response =
        existingId === null
          ? await forwardsApi.createPool(request)
          : await forwardsApi.updatePool(existingId, request, options);
      poolId.value = response.data.pool.id;
      warnings.value = response.data.warnings;
      savedForm.value = formFromPool(response.data.pool);
      form.value = formFromPool(response.data.pool);
      // A create closes the editor, so only an update reads the detail back.
      if (existingId !== null) await refreshDetail(existingId);
      return response.data.pool.id;
    } catch (caught) {
      saveError.value = caught;
      reportUnexpected(caught);
      return null;
    } finally {
      saving.value = false;
    }
  };

  /** The user's own name, which no generated one replaces from now on. */
  const setName = (name: string) => {
    nameEdited.value = true;
    form.value.name = name;
  };

  /** Selects a listing kind; each kind returns on the width it was last on. */
  const setListingKind = (kind: PoolListingKind) => {
    form.value.listing_kind = kind;
    if (kind !== "pay_as_produced") form.value.last_listed_kind = kind;
  };

  /** Selects a width of the listed kind — from pay-as-produced, of the kind it was entered from. */
  const setEntryWidth = (width: PeriodWidth) => {
    const kind = form.value.last_listed_kind;
    form.value.listing_kind = kind;
    form.value.entry_widths[kind] = width;
  };

  const discard = () => {
    form.value = cloneForm(savedForm.value);
    nameEdited.value = poolId.value !== null;
    saveError.value = null;
  };

  /** @returns true when the pool was deleted. */
  const remove = async (): Promise<boolean> =>
    runAction("delete", () => forwardsApi.deletePool(requireId()));

  /** @returns true when the pool was archived. */
  const archive = async (): Promise<boolean> =>
    runAction("archive", () => forwardsApi.archivePool(requireId()));

  /** @returns true when the pool was unarchived. */
  const unarchive = async (): Promise<boolean> =>
    runAction("unarchive", () => forwardsApi.unarchivePool(requireId()));

  // lodash timers are not Vue effects, so ending the session would leave a pending call to fire
  // after the editor is gone.
  onScopeDispose(() => {
    schedulePreview.cancel();
    scheduleCandidateDevices.cancel();
  });

  watch(
    form,
    () => {
      previewPending.value = true;
      schedulePreview();
    },
    { deep: true }
  );

  // A pool without companies has nothing to name yet; a name in the form then would only mark the
  // untouched wizard as changed.
  watch(generatedName, (name) => {
    if (!nameEdited.value && form.value.member_company_ids.length > 0)
      form.value.name = name;
  });

  // Only the membership decides the candidate devices, so the panel does not refetch on every
  // keystroke in the rest of the form.
  watch(
    () => [...form.value.member_company_ids].sort().join(","),
    () => {
      pruneAllowlistToMembers();
      devicesLoading.value =
        (isNew.value || membershipEdited.value) &&
        form.value.member_company_ids.length > 0;
      scheduleCandidateDevices();
    }
  );

  /**
   * Drops the devices of companies the form no longer names from the allowlist.
   *
   * A dropped company's rows leave the panel with it, so an id left behind could not be unticked
   * and the save stayed blocked on "not registered devices of this pool's companies" with nothing
   * to click (RV-pools-08). Ids of devices this editor has never seen are kept: they belong to the
   * saved pool and are not this membership change's to discard.
   */
  function pruneAllowlistToMembers() {
    const members = new Set(form.value.member_company_ids);
    // Every row the editor has seen, not only the ones on show: an edited membership swaps the
    // panel to the candidate rows, and the saved pool's rows are what the allowlist was built from.
    const seen = [...(detail.value?.devices ?? []), ...candidateDevices.value];
    const dropped = new Set(
      seen
        .filter((device) => !members.has(device.company_id))
        .map((device) => device.device_id)
    );
    if (dropped.size === 0) return;

    const kept = form.value.allowlist_device_ids.filter(
      (deviceId) => !dropped.has(deviceId)
    );
    if (kept.length !== form.value.allowlist_device_ids.length)
      form.value.allowlist_device_ids = kept;
  }

  async function readPool(id: number) {
    const [detailResponse, commitmentsResponse] = await Promise.all([
      forwardsApi.getPool(id),
      forwardsApi.listPoolCommitments(id),
    ]);
    detail.value = detailResponse.data;
    commitments.value = commitmentsResponse.data;
    form.value = formFromPool(detailResponse.data.pool);
    savedForm.value = formFromPool(detailResponse.data.pool);
    loadError.value = null;
  }

  function reportUnexpected(error: unknown) {
    if (!isDomainError(error)) globalErrorHandler(error);
  }

  function requireId(): number {
    if (poolId.value === null)
      throw new Error("The pool has not been saved yet.");
    return poolId.value;
  }

  async function runAction(
    kind: "delete" | "archive" | "unarchive",
    call: () => Promise<unknown>
  ): Promise<boolean> {
    busyAction.value = kind;
    saveError.value = null;
    try {
      await call();
      return true;
    } catch (caught) {
      saveError.value = caught;
      reportUnexpected(caught);
      return false;
    } finally {
      busyAction.value = null;
    }
  }

  return reactive({
    poolId: readonly(poolId),
    form,
    // Not wrapped in `readonly()`: these are deep API payloads and `DeepReadonly` would force a
    // cast at every component boundary that only reads them anyway.
    detail,
    commitments,
    preview,
    candidateDevices,
    /** The form as last saved, or blank for a pool not saved yet. */
    savedForm: computed(() => savedForm.value),
    loading: readonly(loading),
    previewLoading: readonly(previewLoading),
    /** The device panel's rows are being read for a changed membership; stale ones may still show. */
    devicesLoading: readonly(devicesLoading),
    previewBusy,
    saving: readonly(saving),
    busyAction: readonly(busyAction),
    // Computeds rather than `readonly()`: these hold foreign error objects, which a readonly
    // proxy would wrap and so break identity checks and `instanceof` on.
    loadError: computed(() => loadError.value),
    notFound: readonly(notFound),
    nameEdited: readonly(nameEdited),
    saveError: computed(() => saveError.value),
    candidateCompaniesError: computed(() => candidateCompaniesError.value),
    saveErrorMessage,
    warnings: readonly(warnings),
    isNew,
    isDirty,
    /** The listing differs from the saved pool's (always, for a pool not saved yet). */
    listingChanged,
    entryWidth,
    recurringDraft,
    oneOffListings,
    formInvalid,
    minimumPriceProblem,
    productionRangeProblem,
    canEdit,
    membersLocked,
    offersOwnCompaniesOnly,
    canForceSave,
    capabilities,
    blockers,
    previewWarnings,
    devices,
    poolDevices,
    memberCandidates,
    memberNames,
    canSave,
    membersReady,
    listingReady,
    escrowRemaining,
    load,
    reload,
    loadCandidateCompanies,
    refreshPreview,
    refreshCandidateDevices,
    save,
    setName,
    setListingKind,
    setEntryWidth,
    discard,
    remove,
    archive,
    unarchive,
  });
}

function sortedIds(ids: readonly number[]): number[] {
  return [...ids].sort((left, right) => left - right);
}
